import { useCallback, useState } from "react"
import Cropper, { type Area } from "react-easy-crop"
import { HERO_PORTRAIT_ASPECT } from "~/constants/heroPortrait"
import { cropImageFile } from "~/utils/cropImageFile"

interface PhotoCropEditorProps {
  imageSrc: string
  fileName: string
  onConfirm: (file: File, previewUrl: string) => void
  onCancel: () => void
}

export function PhotoCropEditor({ imageSrc, fileName, onConfirm, onCancel }: PhotoCropEditorProps) {
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const onCropComplete = useCallback((_area: Area, pixels: Area) => {
    setCroppedAreaPixels(pixels)
  }, [])

  async function handleConfirm() {
    if (!croppedAreaPixels) return
    setProcessing(true)
    setError(null)
    try {
      const file = await cropImageFile(imageSrc, croppedAreaPixels, fileName)
      const previewUrl = URL.createObjectURL(file)
      onConfirm(file, previewUrl)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not crop photo")
    } finally {
      setProcessing(false)
    }
  }

  return (
    <div className="mt-4 flex flex-col gap-4">
      <p className="text-sm text-[var(--color-text-secondary)]">
        Drag to reposition and use the slider to zoom. Keep {fileName ? "your dog" : "the dog"}&apos;s
        face visible, and leave enough coat and body in the frame for a good portrait.
      </p>
      <div
        data-testid="photo-crop-stage"
        className="relative aspect-[16/10] w-full overflow-hidden rounded-lg bg-[var(--color-bg-primary)]"
      >
        <Cropper
          image={imageSrc}
          crop={crop}
          zoom={zoom}
          minZoom={1}
          maxZoom={1.8}
          restrictPosition
          objectFit="cover"
          aspect={HERO_PORTRAIT_ASPECT}
          onCropChange={setCrop}
          onZoomChange={setZoom}
          onCropComplete={onCropComplete}
        />
      </div>
      <label className="flex flex-col gap-1 text-sm text-[var(--color-text-secondary)]">
        Zoom
        <input
          type="range"
          min={1}
          max={1.8}
          step={0.05}
          value={zoom}
          onChange={(e) => setZoom(Number(e.target.value))}
          className="w-full"
        />
      </label>
      {error && (
        <p className="text-sm text-red-600" role="alert">
          {error}
        </p>
      )}
      <div className="flex gap-2">
        <button
          type="button"
          disabled={processing || !croppedAreaPixels}
          onClick={handleConfirm}
          className="flex-1 rounded-lg bg-[var(--color-text-primary)] px-4 py-2 text-sm font-medium text-[var(--color-surface-card)] disabled:opacity-50"
        >
          {processing ? "Processing…" : "Use this photo"}
        </button>
        <button
          type="button"
          disabled={processing}
          onClick={onCancel}
          className="rounded-lg border border-[var(--color-divider)] px-4 py-2 text-sm"
        >
          Cancel
        </button>
      </div>
    </div>
  )
}
